import { useEffect, useMemo, useState } from "react";
import { Button, Flex, Input, Modal, Select, Text } from "src/elements";
import { SelectOption } from "src/elements/Select/store/types";
import { useTranslation } from "src/i18n";
import { useEditShelfModal } from "./store/hooks";
import { usePogAtomActions } from "src/components/Planogram/store/actions";
import { useModals } from "src/components/Modals";
import { getIncompatibleItemsForShelvesType } from "src/components/Planogram/common/shelfTypeCompatibility";
import { atom } from "jotai";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { DIMENSION } from "src/utils/formatDimension";
import {
  formatMerchandisableSpace,
  getMerchandisableSpaceInfo,
} from "src/components/Planogram/common/merchandisableSpace";
import { hasObstructionSpans } from "@CommonUtils/pog/shelfMerchandising";

const dummyPogAtom = atom<Pog | null>(null);

const positiveDecimalPattern = /^(?:\d+\.?\d*|\.\d+)$/;
type ShelfAlign = "left" | "right" | "between" | "around";

export const parseShelfWidthInput = (value: string) => {
  const trimmedValue = value.trim();

  if (!positiveDecimalPattern.test(trimmedValue)) {
    return null;
  }

  const parsed = Number(trimmedValue);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Math.round(parsed * DIMENSION.INCH);
};

export const parseMerchandisableWidthInput = (value: string, maxWidth: number) => {
  const trimmedValue = value.trim();

  if (!positiveDecimalPattern.test(trimmedValue)) {
    return null;
  }

  const parsed = Number(trimmedValue);
  const width = Math.round(parsed * DIMENSION.INCH);

  return Number.isFinite(parsed) && parsed >= 0 && width <= maxWidth ? width : null;
};

export const ModalEditShelf = () => {
  const { t } = useTranslation("common");
  const { editShelfData, closeEditShelfModal } = useEditShelfModal();

  const activePogAtom = editShelfData ? editShelfData.pogAtom : dummyPogAtom;
  const {
    setShelfWidth,
    setShelfMerchandisableWidth,
    setShelfAlign,
    setShelvesType,
    setShelvesTypeAndUnrangeItems,
  } = usePogAtomActions(activePogAtom);
  const { openConfirmModal } = useModals();

  const [widthInput, setWidthInput] = useState("");
  const [merchandisableWidthInput, setMerchandisableWidthInput] = useState("");
  const [alignInput, setAlignInput] = useState<ShelfAlign>("between");
  const [shelfTypeInput, setShelfTypeInput] = useState<PlanogramShelf["type"]>("REGULAR");
  const [isWidthDirty, setIsWidthDirty] = useState(false);
  const [isAlignDirty, setIsAlignDirty] = useState(false);
  const [isShelfTypeDirty, setIsShelfTypeDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shelfWidth = editShelfData?.shelves[0]?.width;
  const shelfMerchandisableWidth = editShelfData?.shelves[0]
    ? (editShelfData.shelves[0].merchandisableWidth ?? editShelfData.shelves[0].width)
    : undefined;
  const shelfAlign = editShelfData?.shelves[0]?.align;
  const shelfType = editShelfData?.shelves[0]?.type;
  const alignOptions = useMemo<SelectOption<ShelfAlign>[]>(
    () => [
      { value: "left", label: t("editShelfModal.alignLeft") },
      { value: "right", label: t("editShelfModal.alignRight") },
      { value: "between", label: t("editShelfModal.alignBetween") },
      { value: "around", label: t("editShelfModal.alignAround") },
    ],
    [t],
  );
  const shelfTypeOptions = useMemo<SelectOption<PlanogramShelf["type"]>[]>(
    () => [
      { value: "REGULAR", label: t("shelfTypes.regular") },
      { value: "RISER", label: t("shelfTypes.riser") },
      { value: "HANGCELL", label: t("shelfTypes.hangCell") },
    ],
    [t],
  );

  useEffect(() => {
    if (shelfWidth !== undefined) {
      setWidthInput((shelfWidth / 100).toString());
      setError(null);
    }
    if (shelfMerchandisableWidth !== undefined) {
      setMerchandisableWidthInput((shelfMerchandisableWidth / 100).toString());
    }
    setAlignInput(shelfAlign ?? "between");
    setShelfTypeInput(shelfType ?? "REGULAR");
    setIsWidthDirty(false);
    setIsAlignDirty(false);
    setIsShelfTypeDirty(false);
  }, [shelfAlign, shelfMerchandisableWidth, shelfType, shelfWidth]);

  if (!editShelfData) {
    return null;
  }

  const { shelves, setIsModified } = editShelfData;
  const isWidthDisabled = shelves.some((shelf) => shelf.mergedLeft || shelf.mergedRight);
  const isSetByObstruction = shelves.some(hasObstructionSpans);
  const isMerchandisableWidthDisabled = isWidthDisabled || isSetByObstruction;
  const merchandisableWidth = shelves[0] ? (shelves[0].merchandisableWidth ?? shelves[0].width) : 0;
  const isMerchandisableWidthDirty =
    merchandisableWidthInput !== (merchandisableWidth / DIMENSION.INCH).toString();
  const merchandisableSpace = shelves[0] ? getMerchandisableSpaceInfo(shelves[0]) : null;

  const onConfirm = () => {
    const newWidth = parseShelfWidthInput(widthInput);
    if (!isWidthDisabled && newWidth === null) {
      setError(t("editShelfModal.invalidWidth"));
      return;
    }
    const newMerchandisableWidth = parseMerchandisableWidthInput(
      merchandisableWidthInput,
      Math.min(
        ...shelves.map((shelf) =>
          !isWidthDisabled && isWidthDirty && newWidth !== null ? newWidth : shelf.width,
        ),
      ),
    );
    if (
      !isMerchandisableWidthDisabled &&
      isMerchandisableWidthDirty &&
      newMerchandisableWidth === null
    ) {
      setError(t("editShelfModal.invalidMerchandisableWidth"));
      return;
    }

    // Products whose intended shelf type falls in a different merchandising group
    // (hanging vs shelved) can't live on the new fixture — they're unranged on confirm.
    const incompatibleItems = isShelfTypeDirty
      ? getIncompatibleItemsForShelvesType(shelves, shelfTypeInput)
      : [];

    const commit = () => {
      if (!isWidthDisabled && isWidthDirty && newWidth !== null) {
        shelves.forEach((shelf) => setShelfWidth(shelf, newWidth));
      }

      if (
        !isMerchandisableWidthDisabled &&
        isMerchandisableWidthDirty &&
        newMerchandisableWidth !== null
      ) {
        shelves.forEach((shelf) => setShelfMerchandisableWidth(shelf, newMerchandisableWidth));
      }

      if (isAlignDirty) {
        const shelfGroups = new Map<string, (typeof shelves)[number]>();
        for (const shelf of shelves) {
          const firstShelf = shelf.getFirstShelf();
          shelfGroups.set(firstShelf.uniqueId, firstShelf);
        }
        shelfGroups.forEach((shelf) => {
          shelf.getMergedShelves().forEach((mergedShelf) => setShelfAlign(mergedShelf, alignInput));
        });
      }

      if (isShelfTypeDirty) {
        if (incompatibleItems.length > 0) {
          setShelvesTypeAndUnrangeItems(
            shelves,
            shelfTypeInput,
            incompatibleItems.map((item) => item.productCode),
          );
        } else {
          setShelvesType(shelves, shelfTypeInput);
        }
      }

      if (
        setIsModified &&
        ((!isWidthDisabled && isWidthDirty) ||
          (!isMerchandisableWidthDisabled && isMerchandisableWidthDirty) ||
          isAlignDirty ||
          isShelfTypeDirty)
      ) {
        setIsModified(true);
      }
      closeEditShelfModal();
    };

    if (incompatibleItems.length > 0) {
      const names = incompatibleItems.map((item) => item.name);
      const shownNames = names.slice(0, 5).join(", ");
      const extra =
        names.length > 5
          ? ` ${t("editShelfModal.incompatibleProductsAndMore", { count: names.length - 5 })}`
          : "";
      const newTypeLabel =
        shelfTypeOptions.find((option) => option.value === shelfTypeInput)?.label ?? shelfTypeInput;
      const descriptionParams = {
        count: incompatibleItems.length,
        shelfType: newTypeLabel,
        names: `${shownNames}${extra}`,
      };
      const description =
        incompatibleItems.length === 1
          ? shelves.length === 1
            ? t("editShelfModal.incompatibleProductDescriptionOneShelf", descriptionParams)
            : t("editShelfModal.incompatibleProductDescriptionMultipleShelves", descriptionParams)
          : shelves.length === 1
            ? t("editShelfModal.incompatibleProductsDescriptionOneShelf", descriptionParams)
            : t("editShelfModal.incompatibleProductsDescriptionMultipleShelves", descriptionParams);

      openConfirmModal({
        icon: { name: "warningCircle" },
        title: t("editShelfModal.changeShelfTypeTitle", { shelfType: newTypeLabel }),
        description,
        buttons: [
          { id: "cancel", label: t("cancel"), variant: "inverted" },
          {
            id: "confirm",
            label: t("editShelfModal.changeAndUnrange"),
            color: "primary",
            onClick: commit,
          },
        ],
      });
      return;
    }

    commit();
  };

  return (
    <Modal name="ModalEditShelf" width="320px" title={t("editShelfModal.title")}>
      <Flex column padding="20px" gap="20px">
        <Flex column gap="6px">
          <Text variant="small2" color="primary">
            {t("editShelfModal.widthLabel")}
          </Text>
          <Input
            value={widthInput}
            setValue={(val) => {
              setWidthInput(val);
              setIsWidthDirty(true);
              setError(null);
            }}
            placeholder={t("editShelfModal.widthPlaceholder")}
            isDisabled={isWidthDisabled}
          />
          {isWidthDisabled && (
            <Text variant="caption1" color="textDisabled">
              {t("editShelfModal.mergedWidthDisabled")}
            </Text>
          )}
          {error && (
            <Text variant="caption1" color="red">
              {error}
            </Text>
          )}
        </Flex>

        <Flex column gap="6px">
          <Input
            title={t("editShelfModal.merchandisableWidthLabel")}
            value={merchandisableWidthInput}
            setValue={(val) => {
              setMerchandisableWidthInput(val);
              setError(null);
            }}
            placeholder={t("editShelfModal.merchandisableWidthPlaceholder")}
            isDisabled={isMerchandisableWidthDisabled}
          />
          {isMerchandisableWidthDisabled && (
            <Text variant="caption1" color="textDisabled">
              {isWidthDisabled
                ? t("editShelfModal.mergedMerchandisableWidthDisabled")
                : t("editShelfModal.obstructionMerchandisableWidthDisabled")}
            </Text>
          )}
        </Flex>

        {merchandisableSpace && (
          <Flex column gap="6px">
            <Text variant="small2" color="primary">
              {t("editShelfModal.merchandisableSpaceLabel")}
            </Text>
            <Text variant="small1">{formatMerchandisableSpace(merchandisableSpace)}</Text>
          </Flex>
        )}

        <Flex column gap="6px">
          <Select<ShelfAlign>
            title={t("editShelfModal.alignLabel")}
            value={alignInput}
            setValue={(val) => {
              setAlignInput(val);
              setIsAlignDirty(true);
            }}
            options={alignOptions}
          />
        </Flex>

        <Flex column gap="6px">
          <Select
            title={t("shelfTypes.label")}
            value={shelfTypeInput}
            setValue={(value) => {
              setShelfTypeInput(value);
              setIsShelfTypeDirty(value !== shelfType);
            }}
            options={shelfTypeOptions}
          />
        </Flex>

        <Flex gap="10px" justify="right">
          <Button variant="inverted" onClick={closeEditShelfModal}>
            {t("cancel")}
          </Button>
          <Button onClick={onConfirm}>{t("save")}</Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
